import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, FilePlus2, Lock, Sparkles } from 'lucide-react';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { LoadingState } from '@/components/ui/states';
import { apiMessage } from '../helpers';
import type { Role, RoleDetail } from '../types';

/**
 * §24.2 — the seeded system roles offered as *starting points*.
 *
 * `GET /roles/templates` reads `SYSTEM_ROLES` (minus PLATFORM_ADMIN and every
 * `platform.*` key), so a template is always exactly what the seeder ships. A
 * pick COPIES the key list into a new ordinary custom role: nothing here
 * creates a reference, which is why the copy notice is stated at the moment of
 * choosing rather than buried in a help page. Duplicating an existing role
 * works the same way and is the more common path — cloning a role that already
 * works beats assembling one from 100 checkboxes.
 */

export interface RoleTemplate {
  key: string;
  name: string;
  permissionKeys: string[];
  permissionCount: number;
}

export type StartingPointKind = 'blank' | 'template' | 'role';

export interface StartingPoint {
  kind: StartingPointKind;
  /** Suggests the new role's key; '' for a blank start. */
  key: string;
  name: string;
  permissionKeys: string[];
}

export const BLANK_START: StartingPoint = { kind: 'blank', key: '', name: '', permissionKeys: [] };

export function useRoleTemplates(enabled: boolean) {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<RoleTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!enabled || loaded) return;
    setLoading(true);
    api
      .get<{ templates: RoleTemplate[] }>('/roles/templates')
      .then((res) => {
        setTemplates(res.data.templates ?? []);
        setError(null);
      })
      // A template list that will not load must not block role creation —
      // starting empty is always available.
      .catch((e) => setError(apiMessage(e, t('settings.roles.templatesLoadFailed'))))
      .finally(() => {
        setLoaded(true);
        setLoading(false);
      });
  }, [enabled, loaded, t]);

  return { templates, loading, error };
}

/** `Floor Supervisor` → `FLOOR_SUPERVISOR`, the shape `CreateRoleDto` demands. */
export function suggestKey(name: string): string {
  return name
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^([0-9])/, 'R$1')
    .slice(0, 40);
}

interface Props {
  templates: RoleTemplate[];
  templatesLoading: boolean;
  templatesError: string | null;
  /** Existing tenant roles, system and custom alike — all are duplicable. */
  roles: Role[];
  onPick: (start: StartingPoint) => void;
}

function Card({
  title,
  subtitle,
  badge,
  icon,
  onClick,
  disabled,
}: {
  title: string;
  subtitle: string;
  badge?: ReactNode;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-start gap-3 rounded-lg border p-3 text-start transition hover:border-accent hover:bg-accent-soft/40 disabled:opacity-60"
    >
      <span className="mt-0.5 text-ink-muted">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{title}</span>
          {badge}
        </span>
        <span className="mt-0.5 block text-xs text-ink-muted">{subtitle}</span>
      </span>
    </button>
  );
}

export default function RoleTemplates({
  templates,
  templatesLoading,
  templatesError,
  roles,
  onPick,
}: Props) {
  const { t } = useTranslation();
  const [busyRole, setBusyRole] = useState<string | null>(null);

  /** A role's permission keys only come with the detail read, so fetch on pick. */
  const pickRole = async (role: Role) => {
    setBusyRole(role.id);
    try {
      const { data } = await api.get<RoleDetail>(`/roles/${role.id}`);
      onPick({
        kind: 'role',
        key: role.key,
        name: role.name,
        permissionKeys: data.permissionKeys ?? [],
      });
    } catch {
      // Falls back to an empty copy rather than stranding the user mid-flow.
      onPick({ kind: 'role', key: role.key, name: role.name, permissionKeys: [] });
    } finally {
      setBusyRole(null);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-muted">
        {t('settings.roles.startingPointHint')}
      </p>

      <Card
        icon={<FilePlus2 className="h-4 w-4" />}
        title={t('settings.roles.startBlank')}
        subtitle={t('settings.roles.startBlankDesc')}
        onClick={() => onPick(BLANK_START)}
      />

      <section className="space-y-2">
        <h4 className="text-sm font-semibold">{t('settings.roles.templatesTitle')}</h4>
        {templatesLoading ? (
          <LoadingState label={t('common.loading')} />
        ) : templatesError ? (
          <p className="text-xs text-amber-700 dark:text-amber-400">{templatesError}</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {templates.map((template) => (
              <Card
                key={template.key}
                icon={<Sparkles className="h-4 w-4" />}
                title={template.name}
                subtitle={t(`settings.roles.templates.${template.key}`, '')}
                badge={
                  <Badge tone="neutral">
                    {t('settings.roles.permissionCountShort', { n: template.permissionCount })}
                  </Badge>
                }
                onClick={() =>
                  onPick({
                    kind: 'template',
                    key: template.key,
                    name: template.name,
                    permissionKeys: template.permissionKeys,
                  })
                }
              />
            ))}
          </div>
        )}
      </section>

      {roles.length > 0 && (
        <section className="space-y-2">
          <h4 className="text-sm font-semibold">{t('settings.roles.existingTitle')}</h4>
          <div className="grid gap-2 sm:grid-cols-2">
            {roles.map((role) => (
              <Card
                key={role.id}
                icon={<Copy className="h-4 w-4" />}
                title={role.name}
                subtitle={
                  role.description ??
                  t('settings.roles.permissionCountShort', { n: role._count?.permissions ?? 0 })
                }
                badge={
                  role.isSystem ? (
                    <Badge tone="info">
                      <Lock className="me-1 h-3 w-3" />
                      {t('settings.roles.system')}
                    </Badge>
                  ) : undefined
                }
                disabled={busyRole !== null}
                onClick={() => void pickRole(role)}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
